import { Box, Button, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { search } from 'store/discipline/incident'
import { useDispatch, useSelector } from 'react-redux'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBasePath } from 'components/Providers'
import moment from 'moment'
import NoRecords from 'components/NoRecords'
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt'
import { ROWS_PER_PAGE_OPTIONS } from 'constants'

const DEFAULT_PAGE_SIZE = 10

const personName = person => (person ? `${person.lastName ?? ''}, ${person.firstName ?? ''}` : '')

export default function IncidentsList({ query, onQueryChange, canEdit }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const basePath = useBasePath()
  const disciplineIncident = useSelector(state => state.discipline.disciplineIncident.list)
  const count = useSelector(state => state.discipline.disciplineIncident.count)
  const searchLoading = useSelector(state => state.discipline.disciplineIncident.searchLoading)
  const allStudents = useSelector(state => state.lookup.allStudents.rows)

  const { startDate, endDate } = query ?? {}
  const queryKey = JSON.stringify(query ?? {})

  const paginationModel = {
    page: query?.limit ? query.offset / query.limit : 0,
    pageSize: query?.limit || DEFAULT_PAGE_SIZE,
  }

  useEffect(() => {
    if (startDate && endDate) {
      dispatch(search(query))
    }
  }, [dispatch, queryKey])

  const handlePaginationModelChange = newModel => {
    const { page, pageSize } = newModel
    onQueryChange({
      ...query,
      limit: pageSize,
      offset: pageSize !== paginationModel.pageSize ? 0 : pageSize * page,
    })
  }

  const enrollmentByPersonId = useMemo(() => {
    const map = new Map()
    ;(allStudents ?? []).forEach(student => map.set(student.personId, student.academicYearEnrollment))
    return map
  }, [allStudents])

  const columns = [
    {
      field: 'student',
      headerName: t('Student'),
      flex: 1.3,
      minWidth: 160,
      valueGetter: (value, row) => personName(row?.student),
    },
    {
      field: 'class',
      headerName: t('Class'),
      flex: 0.8,
      minWidth: 100,
      valueGetter: (value, row) =>
        row?.student?.gradeSection || enrollmentByPersonId.get(row?.student?.id)?.section?.gradeSection || '',
    },
    {
      field: 'staff',
      headerName: t('Staff'),
      flex: 1.3,
      minWidth: 160,
      valueGetter: (value, row) => personName(row?.staff),
    },
    {
      field: 'incidentDate',
      headerName: t('Date'),
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Stack direction="column" spacing={0.5}>
          <Typography variant='body2'>{moment(row?.incidentDate).format('MM/DD/YYYY')}</Typography>
          <Typography variant='body2'>{moment(row?.incidentDate).format('hh:mm A')}</Typography>
        </Stack>
      ),
    },
    {
      field: 'incident',
      headerName: t('Incident'),
      flex: 1.5,
      minWidth: 160,
      valueGetter: (value, row) => row?.incidentItem?.name ?? '',
    },
    {
      field: 'point',
      headerName: t('Point'),
      width: 100,
      valueGetter: (value, row) => row?.incidentItem?.point,
      renderCell: ({ row }) => (
        <Chip
          label={row?.incidentItem?.point}
          color={row?.incidentItem?.point >= 0 ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    ...(canEdit
      ? [
          {
            field: 'actions',
            headerName: t('Actions'),
            width: 110,
            sortable: false,
            renderCell: ({ row }) => (
              <Button variant="outlined" size="small" onClick={() => navigate(`${basePath}/${row.id}`)}>
                {t('More')}
              </Button>
            ),
          },
        ]
      : []),
  ]

  return (
    <Box sx={{ display: 'flex', padding: '10px', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <CardContent sx={{ pr: 0, pl: 0 }}>
            <DataGrid
              rows={disciplineIncident ?? []}
              columns={columns}
              getRowId={row => row.id ?? row.uid}
              disableColumnMenu
              rowHeight={64}
              loading={searchLoading || !disciplineIncident}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              paginationMode="server"
              rowCount={count || 0}
              pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
              pagination
              slots={{
                noRowsOverlay: () => (
                  <NoRecords
                    message={`No Incidents between ${moment(startDate).format('MM-DD-YYYY')} and ${moment(endDate).format('MM-DD-YYYY')}`}
                    icon={<SentimentSatisfiedAltIcon sx={{ fontSize: 40, mb: 1, color: 'grey' }} />}
                  />
                ),
              }}
              slotProps={{ loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' } }}
              sx={{
                height: 'calc(100vh - 355px)',
                overflow: 'auto',
                '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
              }}
            />
          </CardContent>
        </Grid>
      </Grid>
    </Box>
  )
}
